import React, { useContext, useMemo } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { DataContext } from '../data/DataContext';
import { DataContextType } from '../data_types/interfaces';
import { NormalizedGameData } from '../data_types/types';
import { formatHistoryDate } from '../data/formatting';
import { getRecentForm, placeLabel, placeNumber } from '../data/player_insights';
import { RoundWindowSelect, useRoundWindow } from '../shared/RoundWindow';
import { RecordText } from '../shared/RivalComponents';
import PlacementDoughnut from '../charts/PlacementDoughnut';
import { DS, Overline, Panel, enterSx, formatSigned, numericSx, trendColor } from '../shared/Dashboard';

const placeColor = (position: string) => {
    switch (placeNumber(position)) {
        case 1: return DS.gold;
        case 2: return DS.silver;
        case 3: return DS.bronze;
        case 4: return DS.textMuted;
        default: return DS.loss;
    }
};

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box>
        <Overline>{label}</Overline>
        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: DS.text, mt: 0.25, ...numericSx }}>{value}</Typography>
    </Box>
);

/**
 * Rating over the form window, starting from the rating before the first round.
 * The line stretches to fill the box; dots and labels are HTML so they don't distort.
 */
const RatingTrajectory: React.FC<{ rounds: NormalizedGameData[] }> = ({ rounds }) => {
    const points = useMemo(() => {
        if (!rounds.length) return [];
        const start = rounds[0].currentRating - rounds[0].ratingChange;
        return [start, ...rounds.map(r => r.currentRating)];
    }, [rounds]);

    if (points.length < 2) return null;

    const min = Math.min(...points);
    const max = Math.max(...points);
    const span = max - min || 1;
    const x = (i: number) => (i / (points.length - 1)) * 100;
    const y = (v: number) => 8 + (1 - (v - min) / span) * 84;
    const path = points.map((v, i) => `${i ? 'L' : 'M'}${x(i)},${y(v)}`).join(' ');
    const net = points[points.length - 1] - points[0];
    const color = trendColor(net);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 120 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                <Overline>Rating trajectory</Overline>
                <Typography sx={{ fontSize: '12px', color: DS.textMuted, ...numericSx }}>
                    {points[0]} → <Box component="span" sx={{ color, fontWeight: 700 }}>{points[points.length - 1]}</Box>
                    <Box component="span" sx={{ color: DS.textFaint, ml: 1 }}>range {min}–{max}</Box>
                </Typography>
            </Box>
            <Box sx={{
                position: 'relative',
                flex: 1,
                minHeight: 90,
                borderRadius: '10px',
                background: DS.inset,
                border: `1px solid ${DS.border}`,
            }}>
                <Box sx={{ position: 'absolute', inset: '0 14px' }}>
                    <svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%" style={{ display: 'block', overflow: 'visible' }}>
                        <path d={`${path} L100,100 L0,100 Z`} fill={alpha(color, 0.12)} />
                        <path d={path} fill="none" stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
                    </svg>
                    {points.map((v, i) => (
                        <Tooltip key={i} arrow title={i === 0 ? `Start · ${v}` : `Round #${rounds[i - 1].matchNum} · ${v} (${formatSigned(rounds[i - 1].ratingChange)})`}>
                            <Box sx={{
                                position: 'absolute',
                                left: `${x(i)}%`,
                                top: `${y(v)}%`,
                                width: 8,
                                height: 8,
                                ml: '-4px',
                                mt: '-4px',
                                borderRadius: '50%',
                                background: DS.surface,
                                border: `2px solid ${i === 0 ? DS.neutral : trendColor(rounds[i - 1].ratingChange)}`,
                            }} />
                        </Tooltip>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

const RecentForm = () => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { historyData, summaryData } = context || {};

    const { roundWindow } = useRoundWindow();
    const form = useMemo(() => getRecentForm(historyData, roundWindow), [historyData, roundWindow]);
    const seasonAverage: number = summaryData.mean_points_gained_per_round;
    const momentum = form.averageChange - seasonAverage;
    // Pace relative to the season average, not absolute direction: a +40 run can still be below season pace.
    const momentumLabel = Math.abs(momentum) < 1 ? 'On season pace' : momentum > 0 ? 'Above season pace' : 'Below season pace';
    const momentumTooltip = `${formatSigned(form.averageChange, 1)} Elo/round over these ${form.rounds.length} rounds vs ${formatSigned(seasonAverage, 1)} season average`;

    return (
        <Panel
            title="Recent form"
            subtitle={`${form.rounds.length} rounds · newest last`}
            grid={{ xs: 12, md: 8 }}
            delay={0.05}
            action={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip arrow title={momentumTooltip}>
                        <Box sx={{
                            px: 1.25,
                            py: 0.5,
                            borderRadius: '999px',
                            background: alpha(trendColor(Math.abs(momentum) < 1 ? 0 : momentum), 0.14),
                            color: trendColor(Math.abs(momentum) < 1 ? 0 : momentum),
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'default',
                        }}>
                            {momentumLabel}
                        </Box>
                    </Tooltip>
                    <RoundWindowSelect />
                </Box>
            }
        >
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(5, 1fr)', sm: 'repeat(10, 1fr)' },
                gap: 1,
            }}>
                {form.rounds.map((round, i) => {
                    const color = placeColor(round.position);
                    const change = round.ratingChange;
                    return (
                        <Tooltip
                            key={round.matchNum}
                            arrow
                            title={`Round #${round.matchNum} · ${formatHistoryDate(round.date)} · Rating ${round.currentRating}`}
                        >
                            <Box sx={{
                                textAlign: 'center',
                                py: 1.25,
                                borderRadius: '10px',
                                background: DS.inset,
                                border: `1px solid ${DS.border}`,
                                borderBottom: `3px solid ${trendColor(change)}`,
                                cursor: 'default',
                                ...enterSx(0.1 + i * 0.03),
                            }}>
                                <Typography sx={{ fontSize: '15px', fontWeight: 800, color, lineHeight: 1.2 }}>
                                    {placeLabel(round.position)}
                                </Typography>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: trendColor(change), mt: 0.25, ...numericSx }}>
                                    {formatSigned(change)}
                                </Typography>
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>

            <Box sx={{
                mt: 2.5,
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
                gap: 2,
            }}>
                <Stat label="Rating change" value={<span style={{ color: trendColor(form.ratingChange) }}>{formatSigned(form.ratingChange)}</span>} />
                <Stat
                    label="W-T-L"
                    value={
                        <Tooltip arrow title="Win = outright 1st · Tie = tied for 1st · Loss = anything else">
                            <span><RecordText {...form.record} size={18} /></span>
                        </Tooltip>
                    }
                />
                <Stat
                    label="Elo / round"
                    value={
                        <>
                            <span style={{ color: trendColor(form.averageChange) }}>{formatSigned(form.averageChange, 1)}</span>
                            <Typography component="span" sx={{ fontSize: '12px', color: DS.textMuted, ml: 0.75 }}>
                                vs {formatSigned(seasonAverage, 1)}
                            </Typography>
                        </>
                    }
                />
            </Box>

            {form.rounds.length > 0 && (
                <Box sx={{
                    mt: 2.5,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '3fr 2fr' },
                    gap: 3,
                    ...enterSx(0.2),
                }}>
                    <RatingTrajectory rounds={form.rounds} />
                    <Box>
                        <Overline>Placements</Overline>
                        <Box sx={{ mt: 1.5 }}>
                            <PlacementDoughnut buckets={form.placements} />
                        </Box>
                    </Box>
                </Box>
            )}
        </Panel>
    );
};

export default RecentForm;
